import { useState } from 'react';
import SEOHead from '../components/SEOHead';
import seoData from '../seo/contact.json';
import './Contact.css';

const serviceOptions = [
  'Tax Preparation & Planning',
  'Accounting Services',
  'Bookkeeping / Write-up',
  'Payroll Services',
  'Financial & Retirement Planning',
  'Estate & Trust Planning',
  'Business Valuation',
  'Audit Assistance',
  'QuickBooks Assistance',
  'Other',
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'contact',
          ...formData,
        }).toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setError('Something went wrong. Please try again or call us directly.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again or call us directly.');
    }
  };

  return (
    <>
      <SEOHead seo={seoData} />

      {/* ===== PAGE HEADER ===== */}
      <section className="page-header">
        <div className="page-header__bg" />
        <div className="container">
          <div className="page-header__content">
            <span className="section-label" style={{ color: 'var(--gold-light)' }}>Contact Us</span>
            <h1 className="page-header__title">Let's Start a Conversation</h1>
            <p className="page-header__subtitle">
              We look forward to hearing from you. Reach out to schedule a consultation or ask any questions.
            </p>
          </div>
        </div>
      </section>

      {/* ===== CONTACT CONTENT ===== */}
      <section className="section contact-section">
        <div className="container">
          <div className="contact__layout">
            {/* Contact Info */}
            <div className="contact__info">
              <h2 className="contact__info-title">Get In Touch</h2>
              <p className="contact__info-text">
                Whether you have a question about our services, need a consultation, 
                or want to discuss your financial needs — we're here to help.
              </p>

              <div className="contact__cards">
                <div className="contact__card">
                  <div className="contact__card-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  </div>
                  <div>
                    <h4>Phone</h4>
                    <a href="tel:+18183430080">(818) 343-0080</a>
                    <span className="contact__card-sub">Fax: (818) 343-1054</span>
                  </div>
                </div>

                <div className="contact__card">
                  <div className="contact__card-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </div>
                  <div>
                    <h4>Email</h4>
                    <a href="mailto:vahan@gelejianfs.com">vahan@gelejianfs.com</a>
                    <span className="contact__card-sub">
                      <a href="mailto:ourtaxguy@mail.com">ourtaxguy@mail.com</a>
                    </span>
                  </div>
                </div>

                <div className="contact__card">
                  <div className="contact__card-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </div>
                  <div>
                    <h4>Office</h4>
                    <p>20121 Ventura Blvd Ste 310</p>
                    <p>Woodland Hills, CA 91364</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="contact__map">
                <iframe
                  title="Gelejian Financial Services Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3302.7!2d-118.6!3d34.168!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s20121+Ventura+Blvd+Suite+310+Woodland+Hills+CA+91364!5e0!3m2!1sen!2sus!4v1700000000000"
                  width="100%"
                  height="240"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact__form-wrap">
              <div className="contact__form-card">
                <h3 className="contact__form-title">Send Us a Message</h3>
                <p className="contact__form-subtitle">
                  Fill out the form below and we'll get back to you promptly.
                </p>

                {submitted ? (
                  <div className="contact__success">
                    <div className="contact__success-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    </div>
                    <h4>Thank You!</h4>
                    <p>Your message has been sent successfully. We'll get back to you shortly.</p>
                  </div>
                ) : (
                  <form
                    name="contact"
                    method="POST"
                    data-netlify="true"
                    data-netlify-honeypot="bot-field"
                    onSubmit={handleSubmit}
                    className="contact__form"
                  >
                    <input type="hidden" name="form-name" value="contact" />
                    <div hidden>
                      <input name="bot-field" />
                    </div>

                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone">Phone</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(818) 555-0123"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="service">Service of Interest</label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                      >
                        <option value="">Select a service...</option>
                        {serviceOptions.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows="5"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us how we can help you..."
                      />
                    </div>

                    {error && <p className="form-error">{error}</p>}

                    <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                      Send Message
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
